/**
 * PostLoginChecker
 * 
 * Unified component that handles all post-login checks and modals.
 * Lives at the App level to work regardless of where the user logs in from.
 * 
 * Checks are run in priority order:
 * 1. Terms Acceptance (blocking) - Only if TERMS_ACCEPTANCE_MODE is 'hard'
 * 2. Passkey Promotion (optional) - Suggests biometric login
 * 3. [Future] Other promotional modals
 * 
 * Flow:
 * 1. AuthContext.login() sets sessionStorage 'just_logged_in' = 'true'
 * 2. This component detects it after auth state changes
 * 3. Runs checks in order, showing modals as needed
 */
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/core/contexts/AuthContext';
import { usePreferences } from '@/core/contexts/PreferencesContext';
import { usePasskey } from '@/features/auth/passkey/usePasskey';
import { PasskeyPromotionModal } from '@/features/auth/passkey/PasskeyPromotionModal';
import { TermsAcceptanceModal } from './TermsAcceptanceModal';

const JUST_LOGGED_IN_KEY = 'just_logged_in';

export function PostLoginChecker() {
  const { isAuthenticated, user } = useAuth();
  const { consent, refetch: refetchSession } = usePreferences();
  const { shouldShowPromotion } = usePasskey();
  
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const hasCheckedRef = useRef(false);

  // Main effect: detect fresh login and run checks
  useEffect(() => {
    // Skip if already checked this session
    if (hasCheckedRef.current) return;
    
    // Need to be authenticated with user loaded
    if (!isAuthenticated || !user) return;
    
    // Check if this is a fresh login
    const justLoggedIn = sessionStorage.getItem(JUST_LOGGED_IN_KEY);
    if (!justLoggedIn) return;
    
    // Clear flag and mark as checked
    sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
    hasCheckedRef.current = true;
    
    // Run checks after a short delay to let the page settle
    const runChecks = async () => {
      try {
        // Check 1: Terms Acceptance (blocking)
        // consent comes from /session/init via PreferencesContext
        if (consent?.requiresTermsAcceptance) {
          setShowTermsModal(true);
          return;
        }
        
        // Check 2: Passkey Promotion (optional)
        const shouldShow = await shouldShowPromotion();
        if (shouldShow) {
          setShowPasskeyModal(true);
        }
      } catch (err) {
        console.error('[PostLoginChecker] Error:', err);
      }
    };
    
    // Delay to let the page load
    setTimeout(runChecks, 2000);
  }, [isAuthenticated, user, consent, shouldShowPromotion]);

  // Reset when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      hasCheckedRef.current = false;
      setShowTermsModal(false);
      setShowPasskeyModal(false);
    }
  }, [isAuthenticated]);

  // Handle terms accepted
  const handleTermsAccepted = async () => {
    setShowTermsModal(false);
    // Refetch session to update consent status
    await refetchSession();
    
    // After terms accepted, check for passkey
    setTimeout(async () => {
      const shouldShow = await shouldShowPromotion();
      if (shouldShow) {
        setShowPasskeyModal(true);
      }
    }, 500);
  };

  // Handle passkey modal closed
  const handlePasskeyClose = () => {
    setShowPasskeyModal(false);
  };

  return (
    <>
      {/* Terms Acceptance Modal - Blocking */}
      <TermsAcceptanceModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccepted}
      />
      
      {/* Passkey Promotion Modal - Optional */}
      <PasskeyPromotionModal
        isOpen={showPasskeyModal}
        onClose={handlePasskeyClose}
        onSuccess={handlePasskeyClose}
      />
    </>
  );
}

/**
 * Helper function to mark that a login just happened.
 * Call this from AuthContext.login() after successful authentication.
 */
export function markJustLoggedIn() {
  sessionStorage.setItem(JUST_LOGGED_IN_KEY, 'true');
}

